import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    BuildingStorefrontIcon,
    AcademicCapIcon,
    UserGroupIcon,
    WrenchScrewdriverIcon,
    CheckIcon,
} from "@heroicons/react/24/outline";

const useCases = [
    {
        title: "E-commerce & Retail",
        description: "Boost sales and reduce support tickets with intelligent product recommendations and order support.",
        icon: BuildingStorefrontIcon,
        color: "from-blue-500 to-cyan-500",
        benefits: [
            "24/7 product support and recommendations",
            "Automated order status updates",
            "Return and refund assistance",
            "Shopping cart recovery",
            "Personalized product suggestions"
        ],
        example: "A customer asks about return policy, and the AI instantly provides detailed information and initiates the return process."
    },
    {
        title: "Education & Training",
        description: "Provide instant student support and course guidance with AI-powered educational assistance.",
        icon: AcademicCapIcon,
        color: "from-purple-500 to-pink-500",
        benefits: [
            "Instant student support and FAQ",
            "Course recommendations and guidance",
            "Technical assistance and troubleshooting",
            "Learning path optimization",
            "Assignment and deadline reminders"
        ],
        example: "A student asks about course prerequisites, and the AI provides a personalized learning path with relevant resources."
    },
    {
        title: "Customer Support",
        description: "Transform your customer service with AI that handles 80% of common inquiries automatically.",
        icon: UserGroupIcon,
        color: "from-green-500 to-emerald-500",
        benefits: [
            "Instant response to common questions",
            "Intelligent ticket routing",
            "Multi-language support",
            "Escalation to human agents",
            "Customer satisfaction tracking"
        ],
        example: "A customer reports a login issue, and the AI provides troubleshooting steps before escalating to a human agent."
    },
    {
        title: "SaaS & Technology",
        description: "Provide instant technical support and onboarding assistance for your software products.",
        icon: WrenchScrewdriverIcon,
        color: "from-orange-500 to-red-500",
        benefits: [
            "Technical support and troubleshooting",
            "Product onboarding and tutorials",
            "Feature explanations and demos",
            "Integration assistance",
            "Bug reporting and status updates"
        ],
        example: "A user asks how to integrate the API, and the AI provides step-by-step instructions with code examples."
    },
    {
        title: "Healthcare & Wellness",
        description: "Provide 24/7 health information and appointment scheduling with AI-powered assistance.",
        icon: UserGroupIcon,
        color: "from-teal-500 to-blue-500",
        benefits: [
            "Appointment scheduling and reminders",
            "Health information and FAQs",
            "Insurance and billing questions",
            "Symptom assessment guidance",
            "Provider recommendations"
        ],
        example: "A patient asks about appointment availability, and the AI shows available slots and books the appointment."
    },
    {
        title: "Real Estate",
        description: "Guide potential buyers and renters through property searches and inquiries 24/7.",
        icon: BuildingStorefrontIcon,
        color: "from-indigo-500 to-purple-500",
        benefits: [
            "Property search assistance",
            "Scheduling viewings and tours",
            "Mortgage and financing information",
            "Neighborhood and school data",
            "Market trend insights"
        ],
        example: "A buyer asks about properties in a specific area, and the AI provides listings with detailed information and scheduling options."
    }
];

// Shared animation variants to match hero/how-it-works
const containerVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { when: "beforeChildren", staggerChildren: 0.06 }
    }
};

const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } }
};

const cardVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const UseCaseCard = ({ useCase, index }: { useCase: any; index: number }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
    const [hovered, setHovered] = useState(false);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const rx = ((y - rect.height / 2) / rect.height) * -6;
        const ry = ((x - rect.width / 2) / rect.width) * 6;
        setTilt({ rx, ry });
    };

    return (
        <motion.div
            custom={index}
            variants={cardVariants}
            className="group relative"
        >
            <motion.div
                ref={ref}
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => { setHovered(false); setTilt({ rx: 0, ry: 0 }); }}
                style={{ transformStyle: "preserve-3d", perspective: 800 }}
                className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-8 hover:border-white/20 hover:bg-white/10 transition-all duration-200"
            >
                {/* soft fade overlay */}
                <motion.div
                    className="pointer-events-none absolute inset-0 bg-black/0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: hovered ? 0.03 : 0 }}
                    transition={{ duration: 0.2 }}
                />
                {/* sheen */}
                <motion.div
                    className="pointer-events-none absolute -inset-1 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "200%" }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                    style={{ transform: "translateZ(60px)" }}
                />
                {/* floating orb behind icon */}
                <motion.div
                    className="absolute -top-10 -left-10 h-32 w-32 rounded-full bg-white/10 blur-2xl"
                    animate={{ scale: hovered ? 1.1 : 1, opacity: hovered ? 0.35 : 0.2 }}
                    transition={{ duration: 0.25 }}
                    style={{ transform: "translateZ(30px)" }}
                />
                <motion.div
                    style={{ transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` }}
                    transition={{ type: "spring", stiffness: 120, damping: 14 }}
                >
                    <div className="flex items-start space-x-4 relative z-10">
                        <div className={`h-12 w-12 flex items-center justify-center rounded-lg bg-gradient-to-r ${useCase.color} shadow-lg`}>
                            <useCase.icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-white mb-2">
                                {useCase.title}
                            </h3>
                            <p className="text-slate-300 mb-4">
                                {useCase.description}
                            </p>
                            <div className="mb-4">
                                <h4 className="text-sm font-semibold text-slate-200 mb-2">Key Benefits:</h4>
                                <ul className="space-y-1">
                                    {useCase.benefits.map((benefit: string, benefitIndex: number) => (
                                        <li key={benefitIndex} className="flex items-center text-sm text-slate-300">
                                            <CheckIcon className="h-4 w-4 text-emerald-400 mr-2 flex-shrink-0" />
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="rounded-lg p-4 border border-white/10 bg-white/5">
                                <h4 className="text-sm font-semibold text-white mb-2">Real Example:</h4>
                                <p className="text-sm text-slate-300">
                                    {useCase.example}
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default function UseCases() {
    return (
        <div id="use-cases" className="py-24 bg-slate-900 relative overflow-hidden">
            {/* Subtle background accents */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
                <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
            </div>
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={containerVariants}
                className="mx-auto max-w-7xl px-6 lg:px-8"
            >
                <div className="mx-auto max-w-2xl text-center">
                    <motion.h2 variants={fadeUp} className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        Perfect for every industry
                    </motion.h2>
                    <motion.p variants={fadeUp} className="mt-6 text-lg leading-8 text-slate-300">
                        Whether you're in e-commerce, healthcare, education, or any other industry, 
                        FloGPT adapts to your specific needs and business processes.
                    </motion.p>
                </div>

                {/* Use case cards */}
                <motion.div variants={containerVariants} className="mx-auto mt-16 max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {useCases.map((useCase, index) => (
                            <UseCaseCard key={useCase.title} useCase={useCase} index={index} />
                        ))}
                    </div>
                </motion.div>

                {/* Industry stats */}
                <motion.div variants={containerVariants} className="mt-20 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-8">
                    <div className="text-center mb-8">
                        <motion.h3 variants={fadeUp} className="text-2xl font-bold text-white mb-2">
                            Trusted across industries
                        </motion.h3>
                        <motion.p variants={fadeUp} className="text-slate-300">
                            See how different businesses are transforming their customer experience
                        </motion.p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <motion.div variants={fadeUp} className="text-center">
                            <div className="text-3xl font-bold text-white mb-2">70%</div>
                            <div className="text-sm text-slate-300">Reduction in support tickets</div>
                        </motion.div>
                        <motion.div variants={fadeUp} className="text-center">
                            <div className="text-3xl font-bold text-white mb-2">24/7</div>
                            <div className="text-sm text-slate-300">Customer support availability</div>
                        </motion.div>
                        <motion.div variants={fadeUp} className="text-center">
                            <div className="text-3xl font-bold text-white mb-2">5min</div>
                            <div className="text-sm text-slate-300">Average setup time</div>
                        </motion.div>
                        <motion.div variants={fadeUp} className="text-center">
                            <div className="text-3xl font-bold text-white mb-2">95%</div>
                            <div className="text-sm text-slate-300">Customer satisfaction rate</div>
                        </motion.div>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
}
